/**
 * 会话事件流的 React 接线。判断全在 `stream.ts` 的纯函数里，这里只管连。
 *
 * # 为什么 link 由 readyState 推，而不是由「有没有收到消息」推
 *
 * 因为「一直没消息」和「连接断了」是两件事：前者是安静，后者是不知道。
 * 用「多久没收到」当判据的话，一个安静的会话会在几十秒后被画成断线，
 * 而一个真的断了的连接在下一条消息到来前都看着正常——两个方向都错。
 *
 * `readyState` 答的正是我们要问的：**此刻还能不能相信「进行中」。**
 *
 * # 浏览器的自动重连只覆盖一半的断法，另一半得我们自己接
 *
 * 判据与理由写在 `stream.ts` 的 `browserGaveUp` 上（连着 2026-09-01 那份
 * 生产日志）。一句话：拿到了响应但它不对（502 / 401 / Content-Type 不对）
 * 时，规范要求浏览器 **fail the connection** —— readyState 落到 CLOSED，
 * `error` 只发一次，**从此不再重试**。于是控制面重启那几秒，页面上的
 * 事件流就永久死了，而屏幕会一直说「暂时收不到最新状态，现场已保留」。
 *
 * **接回来不等于假装在线**：整个退避窗口里 `link` 照旧是 `down`，
 * 「进行中」照旧被撤掉，`opened` 也不回退（那条流确实真开过）。
 * 只有一条新连接**真的 OPEN 并送来事实**，`link` 才回到 `live`。
 */
import { useEffect, useRef, useState } from 'react'

import { mergeRunSteps, parseRunStep, type RunStep } from './runsteps'
import type { LinkState } from './session-state'
import { reviveDelayMs } from '../../app/sse-revive'
import { browserGaveUp, linkStateFrom, nextCursor, streamUrl, type StreamEvent } from './stream'

export interface StreamStatus {
  readonly link: LinkState
  /** 收到的最后一个账本游标。重连时**不用它**——那时浏览器带 Last-Event-ID。 */
  readonly cursor: number | null
  /** 收到过 snapshot 没有。没收到过就说明这条流还没真正开起来。 */
  readonly opened: boolean
  /**
   * 这条会话上收到的**真实**过程步骤，按 sequence 排好、去过重。
   *
   * **只收 `run.` 那一族**，内核自己的事件不进来（判据在 runsteps.ts）。
   * 会话切换时清空——拿 A 的步骤挂在 B 的过程卡下面，
   * 与那个「A 的在线说成 B 的在线」是同一类错。
   */
  readonly steps: readonly RunStep[]
  /**
   * 这条流上**收到过几条事件**。会话切换时归零。
   *
   * # 它存在的唯一理由
   *
   * 聊天消息不走这条流（服务端只推 snapshot / ledger / state，
   * `queries.ts` 顶上那段注释写着这件事）。而 Lulu 每说一句话之前
   * 都会先往账本里记 `run.*`——**也就是说这条流早就知道「有事发生了」，
   * 只是从来没有人拿它去重取消息。**
   *
   * 2026-08-30 生产实测：同一个已打开的页面，浏览器收到了 11 条流事件
   * （request_accepted / tool_started / tool_succeeded …），
   * 而服务端 7 条消息、DOM 里只有 5 条，**十几秒不动**。
   * 事件到了，没人用。
   *
   * 所以这里给一个只增的计数：屏幕那侧盯着它变，按短批次重取投影。
   * **不是把轮询调快**——那是拿更短的定时器盖住问题；
   * 这一条是真的由事件驱动，而且 Last-Event-ID 的续传语义一个字没动。
   *
   * # 为什么是「任何事件」而不是只有 ledger
   *
   * 因为服务端记事件在**写消息之前**（`run.finished` 先落账本，
   * 再 `Say`）。只盯 ledger 的话，最后那次重取可能比消息早几毫秒。
   * 而实测每一批 ledger 后面都跟着一条 `state`——把它也算进来，
   * 那一跳就被覆盖住了。terminal-refresh 会把相邻事件合成一轮请求，
   * 所以这里保持完整感知，不把每条事件放大成三次 GET。
   */
  readonly revision: number
}

const EMPTY_STREAM_STATUS: StreamStatus = {
  link: 'down',
  cursor: null,
  opened: false,
  steps: [],
  revision: 0,
}

/**
 * 退避表与延时算法**定义在 `app/sse-revive.ts`**，这里只是再导出。
 *
 * 三条流（这一条、全站 `/events`、动态中心 `/activity/stream`）用的是
 * 同一套退避，各写一份的那天它们会分家。再导出是为了不改这个模块的
 * 对外形状——`use-stream-revive.test.tsx` 一直从这儿拿 `reviveDelayMs`。
 */
export {
  SSE_REVIVE_BACKOFF_MS as STREAM_REVIVE_BACKOFF_MS,
  reviveDelayMs,
} from '../../app/sse-revive'

interface ScopedStreamStatus {
  readonly conversationId: string | null
  readonly status: StreamStatus
}

export function useConversationStream(conversationId: string | null): StreamStatus {
  const [scoped, setScoped] = useState<ScopedStreamStatus>({
    conversationId: null,
    status: EMPTY_STREAM_STATUS,
  })
  const cursorRef = useRef<number | null>(null)
  const generationRef = useRef(0)

  useEffect(() => {
    const generation = generationRef.current + 1
    generationRef.current = generation
    // **每次会话变了都先清干净。**
    //
    // 上一版只在 `conversationId === null` 那一支里清，于是 A→B 切换时
    // 旧会话的 `opened`/`live`/`cursor` 会一直挂到新 EventSource 打开为止。
    // 那一段里界面拿着 B 的标题、A 的连接状态——**它会把 A 的「在线」
    // 说成 B 的「在线」**，而如果 B 其实连不上，用户看到的是一个
    // 永远不会变的假在线。
    //
    // 游标串得更狠：拿 A 的 sequence 去 B 的账本上续，要么续到一段
    // 不存在的历史，要么跳过 B 开头那几条真事件。
    setScoped({ conversationId, status: EMPTY_STREAM_STATUS })
    cursorRef.current = null

    if (conversationId === null) return
    if (typeof EventSource === 'undefined') return

    // 这条会话此刻**真正在用**的那一根。接回来之后旧的那根不再算数——
    // 所有回调都先对一次身份，否则一根已经被我们放弃的连接还能改 link，
    // 而那正是「旧连接污染新状态」的形状。
    let current: EventSource | null = null
    let reviveTimer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0

    const alive = () => generationRef.current === generation

    const update = (next: (previous: StreamStatus) => StreamStatus) => {
      if (!alive()) return
      setScoped((previous) => {
        if (!alive()) return previous
        const previousStatus =
          previous.conversationId === conversationId ? previous.status : EMPTY_STREAM_STATUS
        return { conversationId, status: next(previousStatus) }
      })
    }

    const connect = () => {
      if (!alive()) return

      // 首次连接不带 cursor；**我们自己接回来的那些带**——
      // 一根全新的 EventSource 没有 Last-Event-ID 可言（那是浏览器
      // 在同一根上续传时才会带的头），不带 cursor 就等于让服务端
      // 从头重放整条账本。`?cursor=` 本来就是为这一刻留的。
      const es = new EventSource(streamUrl(conversationId, cursorRef.current), {
        withCredentials: true,
      })
      current = es

      /** 这一根还算不算数。放弃过的那根，任何回调都到此为止。 */
      const owns = () => alive() && es === current

      const sync = () => {
        if (!owns()) return
        update((previous) => ({
          ...previous,
          link: linkStateFrom(es.readyState),
        }))
      }

      const onEvent = (kind: StreamEvent['kind']) => (e: MessageEvent) => {
        // A→B 切换后，A 的连接可能还有一条已经排进事件队列的回调。
        // 它不能改共享 cursorRef，更不能把 A 的高 revision 写回 B。
        // 被我们放弃的那根同理。
        if (!owns()) return
        // **收到事实才把退避清零，不是「连上了」就清。**
        //
        // 服务端每次连上都先发一条 snapshot，所以一根健康的连接一定会
        // 走到这里；而一根「开了一下就 502」的连接走不到——那时退避
        // 该继续涨，否则服务端在半死不活地翻来覆去时，这里会变成
        // 每秒一次的重连风暴。判据是「它改变了什么」，不是「它发生了」。
        attempt = 0
        const seq = e.lastEventId === '' ? null : Number(e.lastEventId)
        const ev: StreamEvent = {
          kind,
          sequence: Number.isFinite(seq) ? seq : null,
          data: e.data,
        }
        cursorRef.current = nextCursor(cursorRef.current, [ev])
        // **只有 ledger 事件里能解出步骤。** snapshot/state 是投影，
        // 不是「发生过的事」——把它们也解一遍会解出空，白费一次。
        // `data` 的类型是 unknown（它来自网络，那是对的）。
        // 只在它真是字符串时才解——**不 String(...) 强转**：
        // 那会把一个对象转成 "[object Object]" 然后静静地解不出来，
        // 而「解不出来」与「压根不是字符串」在日志里长得一样。
        const step = kind === 'ledger' && typeof ev.data === 'string' ? parseRunStep(ev.data) : null
        update((previous) => ({
          link: linkStateFrom(es.readyState),
          cursor: cursorRef.current,
          opened: true,
          steps: step === null ? previous.steps : mergeRunSteps(previous.steps, [step]),
          // **每一条事件都 +1**，包括解不出步骤的 snapshot / state。
          // 它答的不是「跑到第几步了」，是「那边有动静」。
          revision: previous.revision + 1,
        }))
      }

      es.addEventListener('snapshot', onEvent('snapshot') as EventListener)
      es.addEventListener('ledger', onEvent('ledger') as EventListener)
      es.addEventListener('state', onEvent('state') as EventListener)
      es.onopen = sync
      // **断了就是断了。** link 当场落到 down，界面据此撤掉所有「进行中」——
      // 这一条一个字都没改。改的只是「谁负责接回来」：
      //
      //   readyState === CONNECTING  浏览器自己在接，**我们不许插手**。
      //                              插手就是同一条会话上两根连接。
      //   readyState === CLOSED      浏览器已经放弃了（502 / 401 /
      //                              Content-Type 不对）。不接的话这条流
      //                              就永久死在这儿，而 link 永远是 down。
      es.onerror = () => {
        if (!owns()) return
        sync()
        if (!browserGaveUp(es.readyState)) return
        scheduleRevive()
      }
    }

    const scheduleRevive = () => {
      if (!alive() || reviveTimer !== undefined) return
      const delay = reviveDelayMs(attempt)
      attempt += 1
      reviveTimer = setTimeout(() => {
        reviveTimer = undefined
        if (!alive()) return
        // 上一根已经 CLOSED，close() 只是把话说全。
        current?.close()
        current = null
        connect()
      }, delay)
    }

    connect()

    return () => {
      if (generationRef.current === generation) generationRef.current += 1
      if (reviveTimer !== undefined) clearTimeout(reviveTimer)
      reviveTimer = undefined
      const es = current
      current = null
      es?.close()
    }
  }, [conversationId])

  // useEffect 在 commit 之后才清状态。只靠 effect 的话，A→B 的第一次 render
  // 仍会把 A=100 暴露给 B，然后终态刷新器会拿这个假 revision 请求 B。
  // 返回值先按会话作用域隔离，B 的第一帧因此就是 down/0。
  return scoped.conversationId === conversationId ? scoped.status : EMPTY_STREAM_STATUS
}
